import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreVertical, Edit2, Trash2, ChevronLeft, ChevronRight, Check, ClipboardCheck } from 'lucide-react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { Card, Button, BottomSheet, SegmentedControl } from '../../components/ui';
import { BackButton } from '../../components/ui/BackButton';
import { groupsApi } from '../../api/groups';
import { coachesApi } from '../../api/coaches';
import { athletesApi } from '../../api/athletes';
import { monthlyPaymentsApi } from '../../api/monthlyPayments';
import { formatAge } from '../../utils/dateUtils';
import { formatYearMonth, getCurrentYearMonth, shiftMonth } from '../../types/monthly_payment';
import { GroupScheduleTab } from '../../components/GroupSchedule/GroupScheduleTab';
import { GroupNotesList } from '../../components/GroupNotes/GroupNotesList';
import { AddGroupNoteSheet } from '../../components/GroupNotes/AddGroupNoteSheet';
import { todayISO } from '../../utils/attendance';
import { trainingSessionsApi } from '../../api/trainingSessions';
import { AttendanceJournal } from './AttendanceJournal';
import type { Athlete } from '../../types/api';

export function GroupDetailScreen() {
  const { getCurrentScreen, popScreen, pushScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const groupId = currentScreen.params?.groupId as string | undefined;

  // UI State
  const [activeTab, setActiveTab] = useState<'athletes' | 'schedule' | 'journal' | 'payments' | 'notes'>('athletes');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth());

  // Form State
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedCoachIds, setSelectedCoachIds] = useState<Set<string>>(new Set());

  // Data Queries
  const { data: group, isLoading: isLoadingGroup } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => groupsApi.getById(groupId!),
    enabled: !!groupId,
  });

  const { data: groupCoaches, isLoading: isLoadingCoaches } = useQuery({
    queryKey: ['group-coaches', groupId],
    queryFn: () => coachesApi.getByGroupId(groupId!),
    enabled: !!groupId,
  });

  const { data: allCoaches } = useQuery({
    queryKey: ['coaches', currentAssistantId],
    queryFn: () => coachesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId && isEditing,
  });

  const { data: allAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const groupAthletes = useMemo(() => {
    if (!allAthletes || !groupId) return [];
    return allAthletes.filter((athlete: Athlete) => athlete.group_id === groupId);
  }, [allAthletes, groupId]);

  // Журнал за последний месяц. Тянем только на открытой вкладке: запрос
  // собирает отметки по всем занятиям периода.
  const journalFrom = todayISO(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const journalTo = todayISO();

  const { data: journal, isLoading: isLoadingJournal } = useQuery({
    queryKey: ['attendance-journal', groupId, journalFrom, journalTo],
    queryFn: () => trainingSessionsApi.journal(groupId!, journalFrom, journalTo),
    enabled: !!groupId && activeTab === 'journal',
  });

  // Monthly payments query
  const { data: monthlyPayments, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['monthly-payments', groupId, selectedMonth],
    queryFn: () => monthlyPaymentsApi.getGroupMonthly(groupId!, selectedMonth),
    enabled: !!groupId && activeTab === 'payments',
  });

  // Initialize form data
  useEffect(() => {
    if (group && formData.name === '') {
      setFormData({ name: group.name, description: group.description || '' });
    }
  }, [group]);

  useEffect(() => {
    if (groupCoaches && selectedCoachIds.size === 0) {
      setSelectedCoachIds(new Set(groupCoaches.map((c) => c.id)));
    }
  }, [groupCoaches]);

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async () => {
      await groupsApi.update(groupId!, {
        name: formData.name,
        description: formData.description || undefined,
      });
      const currentCoachIds = new Set(groupCoaches?.map((c) => c.id) || []);
      for (const coachId of currentCoachIds) {
        if (!selectedCoachIds.has(coachId)) {
          await coachesApi.removeFromGroup(coachId, groupId!);
        }
      }
      for (const coachId of selectedCoachIds) {
        if (!currentCoachIds.has(coachId)) {
          await coachesApi.assignToGroup(coachId, { group_id: groupId! });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group-coaches', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups', currentAssistantId] });
      setIsEditing(false);
    },
    onError: (error) => {
      console.error('Failed to update group:', error);
      alert('Не удалось обновить группу. Попробуйте еще раз.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => groupsApi.delete(groupId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups', currentAssistantId] });
      popScreen();
    },
    onError: (error) => {
      console.error('Failed to delete group:', error);
      alert('Не удалось удалить группу. Попробуйте еще раз.');
    },
  });

  const togglePaymentMutation = useMutation({
    mutationFn: ({ athleteId, isPaid }: { athleteId: string; isPaid: boolean }) =>
      monthlyPaymentsApi.togglePayment(groupId!, athleteId, selectedMonth, isPaid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monthly-payments', groupId, selectedMonth] });
    },
    onError: (error) => {
      console.error('Failed to toggle payment:', error);
      alert('Не удалось обновить статус оплаты.');
    },
  });

  // Handlers
  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Название группы обязательно');
      return;
    }
    updateMutation.mutate();
  };

  const handleDelete = () => {
    setIsMenuOpen(false);
    if (confirm(`Вы уверены, что хотите удалить группу "${group?.name}"?`)) {
      deleteMutation.mutate();
    }
  };

  const toggleCoach = (coachId: string) => {
    const newSet = new Set(selectedCoachIds);
    if (newSet.has(coachId)) newSet.delete(coachId);
    else newSet.add(coachId);
    setSelectedCoachIds(newSet);
  };

  const handleBack = () => {
    if (isEditing) setIsEditing(false);
    else popScreen();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: '8px',
  };

  // Loading
  if (isLoadingGroup || isLoadingCoaches) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  // Not found
  if (!group) {
    return (
      <div className="p-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>
            Группа не найдена
          </p>
        </Card>
      </div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2">
            <BackButton onClick={handleBack} />
          </div>

          <Card>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Редактировать группу
            </h2>
            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Название *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Например: Группа начинающих"
                  style={inputStyle}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div>
                <label style={labelStyle}>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Описание группы..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'none' }}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div>
                <label style={labelStyle}>Тренеры</label>
                {allCoaches && allCoaches.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto rounded-xl p-3" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    {allCoaches.map((coach) => (
                      <label key={coach.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer active:opacity-60">
                        <input
                          type="checkbox"
                          checked={selectedCoachIds.has(coach.id)}
                          onChange={() => toggleCoach(coach.id)}
                          disabled={updateMutation.isPending}
                          className="w-5 h-5"
                        />
                        <span className="text-[17px]" style={{ color: 'var(--text-primary)' }}>
                          {coach.name}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[15px] italic" style={{ color: 'var(--text-hint)' }}>
                    Нет доступных тренеров
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending || !formData.name.trim()}
                  loading={updateMutation.isPending}
                  fullWidth
                >
                  Сохранить
                </Button>
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ name: group.name, description: group.description || '' });
                    setSelectedCoachIds(new Set(groupCoaches?.map((c) => c.id) || []));
                  }}
                  disabled={updateMutation.isPending}
                  variant="secondary"
                >
                  Отмена
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // View Mode with Tabs
  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <BackButton onClick={handleBack} />
        <button
          onClick={() => setIsMenuOpen(true)}
          className="p-2 rounded-xl active:opacity-60 transition-opacity"
          style={{ backgroundColor: 'var(--bg-secondary)' }}
        >
          <MoreVertical size={20} style={{ color: 'var(--text-primary)' }} />
        </button>
      </div>

      {/* Перекличка: главный вход на экран занятия */}
      <div className="px-4 pb-3">
        <Button
          variant="primary"
          onClick={() =>
            pushScreen('attendance', { groupId, date: todayISO() })
          }
        >
          <ClipboardCheck size={18} /> Отметить посещаемость
        </Button>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-4">
        <SegmentedControl
          segments={[
            { value: 'athletes', label: 'Состав' },
            { value: 'schedule', label: 'Время' },
            { value: 'journal', label: 'Журнал' },
            { value: 'payments', label: 'Оплаты' },
            { value: 'notes', label: 'Записи' },
          ]}
          value={activeTab}
          onChange={(value) =>
            setActiveTab(value as 'athletes' | 'schedule' | 'journal' | 'payments' | 'notes')
          }
        />
      </div>

      {/* Content */}
      <div className="px-4 space-y-4">
        {activeTab === 'schedule' && group && (
          <GroupScheduleTab group={group} />
        )}

        {activeTab === 'journal' && (
          <Card>
            <AttendanceJournal journal={journal} isLoading={isLoadingJournal} />
          </Card>
        )}

        {activeTab === 'athletes' && (
          <>
            {/* Group Info Card */}
            <Card>
              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {group.name}
              </h2>

              {group.description && (
                <p className="text-[15px] mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {group.description}
                </p>
              )}

              {/* Coaches */}
              <div className="pt-3" style={{ borderTop: '1px solid var(--separator)' }}>
                <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-hint)' }}>
                  Тренеры
                </p>
                {groupCoaches && groupCoaches.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {groupCoaches.map((coach) => (
                      <span
                        key={coach.id}
                        className="text-[13px] px-3 py-1.5 rounded-full font-medium"
                        style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent)' }}
                      >
                        {coach.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[15px] italic" style={{ color: 'var(--text-hint)' }}>
                    Нет закрепленных тренеров
                  </p>
                )}
              </div>
            </Card>

            {/* Athletes List */}
            <Card>
              <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Спортсмены ({groupAthletes.length})
              </h3>

              {groupAthletes.length > 0 ? (
                <div className="space-y-2">
                  {groupAthletes.map((athlete) => (
                    <div
                      key={athlete.id}
                      onClick={() => pushScreen('athlete-detail', { athleteId: athlete.id, fromGroup: groupId })}
                      className="flex items-center justify-between p-3 rounded-xl cursor-pointer active:opacity-60 transition-opacity"
                      style={{ backgroundColor: 'var(--bg-secondary)' }}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[17px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                          {athlete.name}
                        </h4>
                        {athlete.phone && (
                          <p className="text-[13px] truncate" style={{ color: 'var(--text-hint)' }}>
                            {athlete.phone}
                          </p>
                        )}
                      </div>
                      {athlete.date_of_birth && (
                        <span className="text-[15px] ml-3 flex-shrink-0" style={{ color: 'var(--text-hint)' }}>
                          {formatAge(athlete.date_of_birth)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[15px] text-center py-8" style={{ color: 'var(--text-hint)' }}>
                  В группе пока нет спортсменов
                </p>
              )}
            </Card>
          </>
        )}

        {activeTab === 'payments' && (
          /* ===== PAYMENTS TAB ===== */
          <>
            {/* Month selector */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))}
                className="p-2 rounded-xl active:opacity-60"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                <ChevronLeft size={20} style={{ color: 'var(--text-primary)' }} />
              </button>
              <span className="text-[17px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                {formatYearMonth(selectedMonth)}
              </span>
              <button
                onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))}
                className="p-2 rounded-xl active:opacity-60"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                <ChevronRight size={20} style={{ color: 'var(--text-primary)' }} />
              </button>
            </div>

            {/* Summary */}
            {monthlyPayments && (
              <div className="px-1">
                <span className="text-[15px]" style={{ color: 'var(--text-secondary)' }}>
                  Оплатили: {monthlyPayments.paid_count} из {monthlyPayments.total_count}
                </span>
              </div>
            )}

            {/* Athletes payment list */}
            {isLoadingPayments ? (
              <Card>
                <p className="text-[15px] text-center py-8" style={{ color: 'var(--text-hint)' }}>
                  Загрузка...
                </p>
              </Card>
            ) : monthlyPayments && monthlyPayments.athletes.length > 0 ? (
              <Card>
                <div className="space-y-1">
                  {monthlyPayments.athletes.map((item) => {
                    const isPastMonth = selectedMonth < getCurrentYearMonth();
                    const unpaidColor = isPastMonth ? '#FF3B30' : 'var(--text-hint)';
                    const unpaidBg = isPastMonth ? 'rgba(255, 59, 48, 0.1)' : 'var(--bg-secondary)';
                    const unpaidBorder = isPastMonth ? '2px solid rgba(255, 59, 48, 0.3)' : '2px solid var(--separator)';

                    return (
                    <div
                      key={item.athlete_id}
                      className="flex items-center justify-between py-3 px-2 rounded-lg cursor-pointer active:opacity-60"
                      style={{ borderBottom: '1px solid var(--separator)' }}
                      onClick={() =>
                        togglePaymentMutation.mutate({
                          athleteId: item.athlete_id,
                          isPaid: !item.is_paid,
                        })
                      }
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: item.is_paid
                              ? 'rgba(52, 199, 89, 0.15)'
                              : unpaidBg,
                            border: item.is_paid ? 'none' : unpaidBorder,
                          }}
                        >
                          {item.is_paid && <Check size={16} style={{ color: '#34C759' }} />}
                        </div>
                        <span
                          className="text-[15px] font-medium truncate"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {item.athlete_name}
                        </span>
                      </div>
                      <span
                        className="text-[13px] flex-shrink-0 ml-2"
                        style={{
                          color: item.is_paid ? '#34C759' : unpaidColor,
                          fontWeight: !item.is_paid && isPastMonth ? 600 : 400,
                        }}
                      >
                        {item.is_paid ? 'Оплачено' : 'Не оплачено'}
                      </span>
                    </div>
                    );
                  })}
                </div>
              </Card>
            ) : (
              <Card>
                <p className="text-[15px] text-center py-8" style={{ color: 'var(--text-hint)' }}>
                  {groupAthletes.length === 0
                    ? 'В группе нет спортсменов'
                    : 'Нет данных об оплатах'}
                </p>
              </Card>
            )}
          </>
        )}

        {activeTab === 'notes' && groupId && (
          <GroupNotesList
            groupId={groupId}
            onAddNote={() => setIsAddNoteOpen(true)}
          />
        )}
      </div>

      {/* Add Note Sheet */}
      {isAddNoteOpen && groupId && (
        <AddGroupNoteSheet
          groupId={groupId}
          onClose={() => setIsAddNoteOpen(false)}
        />
      )}

      {/* Actions Menu Bottom Sheet */}
      <BottomSheet isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} height="30%">
        <div className="py-4 space-y-2">
          <button
            onClick={() => {
              setIsMenuOpen(false);
              setIsEditing(true);
            }}
            className="w-full flex items-center gap-3 p-4 rounded-xl active:opacity-60 transition-opacity"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'var(--accent-light)' }}
            >
              <Edit2 size={20} style={{ color: 'var(--accent)' }} />
            </div>
            <span className="text-[17px] font-medium" style={{ color: 'var(--text-primary)' }}>
              Редактировать
            </span>
          </button>

          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="w-full flex items-center gap-3 p-4 rounded-xl active:opacity-60 transition-opacity"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255, 59, 48, 0.1)' }}
            >
              <Trash2 size={20} style={{ color: 'var(--danger)' }} />
            </div>
            <span className="text-[17px] font-medium" style={{ color: 'var(--danger)' }}>
              {deleteMutation.isPending ? 'Удаление...' : 'Удалить группу'}
            </span>
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
